import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import API from "../api/axiosConfig"; 

/**
 * UserAutocomplete Component
 * Self-fetches user list on mount and renders an Autocomplete selector.
 *
 * @param {String} label - Field label (default: "Select User")
 * @param {String|Number|Object} value - Primitive key (e.g. email/id) or full user object
 * @param {Function} onChange - Callback returning the updated value
 * @param {String} valueKey - User object property used for value matching (default: "email")
 * @param {String} endpoint - API route to fetch users (default: "/api/users")
 * @param {Boolean} returnFullObject - If true, passes full object to onChange; if false, passes primitive valueKey string/id
 * @param {Boolean} freeSolo - Allow typing custom text not in user list
 */
export default function AutocompleteUsers({
  label = "Select User",
  value = null,
  onChange,
  valueKey = "email",
  endpoint = "/api/users/autocomplete",
  returnFullObject = false,
  freeSolo = false,
  disabled = false,
  error = false,
  helperText = "",
  required = false,
  fullWidth = true,
  placeholder = "",
  ...rest
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch users list once on mount
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    API.get(endpoint)
      .then((res) => {
        if (isMounted) {
          setUsers(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch((err) => {
        console.error(`Failed to fetch user list from ${endpoint}:`, err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [endpoint]);

  const getOptionLabel = (user) => {
    if (!user) return "";
    if (typeof user === "string") return user;
    return user.name || user.email || String(user);
  };

  // Match primitive value prop (like "matt.bressler@s3k.io") back to option object
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === "") {
      return freeSolo ? "" : null;
    }
    if (typeof value === "object") return value;

    const match = users.find((u) => u[valueKey] === value || u === value);
    return match || (freeSolo ? value : null);
  }, [value, users, valueKey, freeSolo]);

  return (
    <Autocomplete
      options={users}
      value={selectedOption}
      loading={loading}
      disabled={disabled || loading}
      freeSolo={freeSolo}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return option === val;
        if (typeof val === "string" || typeof val === "number") {
          return option[valueKey] === val || option === val;
        }
        return option[valueKey] === val[valueKey];
      }}
      onChange={(event, newValue) => {
        if (!onChange) return;

        if (returnFullObject || typeof newValue !== "object" || !newValue) {
          onChange(newValue);
        } else {
          // Extract requested primitive value (e.g. email or id)
          onChange(newValue[valueKey] || "");
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth={fullWidth}
          required={required}
          error={Boolean(error)}
          helperText={helperText || (typeof error === "string" ? error : "")}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
}
